module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('h1', {
    staticClass: "chno"
  }, [_c('a', {
    attrs: {
      "href": "#/Text/SOTL_contents"
    }
  }, [_vm._v("1")])]), _vm._v(" "), _c('h1', {
    staticClass: "chtit",
    attrs: {
      "id": "sigil_toc_id_1"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/Text/SOTL_contents"
    }
  }, [_vm._v("Introduction")])]), _vm._v(" "), _c('p', {
    staticClass: "dpy_epi_pros1"
  }, [_vm._v("Better to reign in Hell, than serve in Heav’n.")]), _vm._v(" "), _c('p', {
    staticClass: "dpy_epi_src"
  }, [_vm._v("(Milton, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Paradise Lost")]), _vm._v(", Book I, line 263)")]), _vm._v(" "), _c('p', {
    staticClass: "fo"
  }, [_vm._v("Satan’s words to his demonic forces in John Milton’s "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Paradise Lost")]), _vm._v(" capture well the lie he has told to humanity throughout the ages – rather than joyfully serve the true and living God, it is better to reject his authority and determine one’s own destiny even if that leads to an eternity in hell. Satan used a form of this lie in the Garden of Eden with Adam and Eve, persuading them to rebel against their sovereign Lord and determine for themselves good and evil. Rather than embrace their identity as servants of the Lord, our first parents chose instead to pursue the subtle but deadly slavery of perceived self-autonomy.")]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("Identity is arguably the defining issue of our contemporary Western culture. At one level humanity has always sought to understand who we are, but developments within the past 500 years have brought the issue of identity to the forefront in fresh ways."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/SOTL_notes#FN_Ch01-1",
      "id": "FR_Ch01-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1")])]), _vm._v(" The question of identity affects us as individuals, as communities and as the people of God. Yet sadly, in the contemporary quest for identity, the Bible is often neglected or ignored, even among professing Christians. But only in Scripture do we find authoritative and infallible answers to the question of who we are as human beings. In order for the church to proclaim and live out a compelling picture of what it means to be human, we must first understand what Scripture says about our identity.")]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("Yet therein lies part of the challenge. The Bible has so much to say about our identity as human beings that it is challenging to know where to begin. But rather than paralyse us, the presence of so much material allows us to explore the question of identity from a wide variety of entry points.")]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("In one sense this book is a contribution to understanding our identity as human beings from a biblical perspective. The specific angle will be to trace the key biblical theme of the servant of the Lord from Genesis to Revelation. I am not arguing that this is the most significant biblical theme for understanding our identity as human beings; rather, I am proposing that it is an important and sometimes overlooked theme that helps us better understand the metanarrative of Scripture as well as our identity as human beings.")]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("But before we begin to trace our theme through the canon, we must first address some preliminary issues.")]), _vm._v(" "), _c('h3', {
    staticClass: "b_head",
    attrs: {
      "id": "sigil_toc_id_2"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/Text/SOTL_contents"
    }
  }, [_vm._v("Preliminary issues")])]), _vm._v(" "), _c('h4', {
    staticClass: "c_head sigil_not_in_toc"
  }, [_vm._v("The nature of servitude in the ancient world")]), _vm._v(" "), _c('p', {
    staticClass: "fo"
  }, [_vm._v("A significant body of literature exists on slavery in the ancient world, the New Testament, and the early church."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/SOTL_notes#FN_Ch01-2",
      "id": "FR_Ch01-2",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2")])]), _vm._v(" The nature of these discussions is at times complex, even to the point of debates over how slavery itself should be defined!"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/SOTL_notes#FN_Ch01-3",
      "id": "FR_Ch01-3",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3")])]), _vm._v(" Further complications arise when studying slavery and servitude in the biblical world from a contemporary perspective that lives under the shadow of the history of race-based slavery in the New World and the present-day forms of slavery that continue. Such contemporary perspectives can blind the reader of Scripture to important differences in slavery in the ancient world. While such differences must not be overemphasized in an attempt to anaesthetize the evils of slavery, responsible interpretation of Scripture must take them into account.")]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("Three differences in particular stand out. The first regards the entry point. While being taken against one’s will (e.g. kidnapping, prisoner of war, etc.) was the most common way to become a slave in the ancient world, some people chose to sell themselves into slavery because of crippling debt. Second, slavery in the ancient world was not necessarily a permanent state of affairs. The Israelites were specifically instructed to release any Hebrew slave from service in the sabbatical year (Lev. 25:35–43)."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/SOTL_notes#FN_Ch01-4",
      "id": "FR_Ch01-4",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("4")])]), _vm._v(" In the Roman Empire, upon their death, masters would sometimes free their slaves; indeed, at one point the practice became so common that the emperor Augustus passed legislation establishing certain limits."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/SOTL_notes#FN_Ch01-5",
      "id": "FR_Ch01-5",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("5")])]), _vm._v(" During the first century it was also possible for some slaves to purchase their freedom, though such a process was usually lengthy and tedious."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/SOTL_notes#FN_Ch01-6",
      "id": "FR_Ch01-6",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("6")])]), _vm._v(" Finally, in the ancient world slaves served in virtually every area of society. While the experience of rural slaves was often similar to the predominantly agricultural nature of New World slavery, urban slaves served in a wide range that ran the gamut of education, government and business."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/SOTL_notes#FN_Ch01-7",
      "id": "FR_Ch01-7",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("7")])])]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("In sharp contrast to our contemporary situation in many parts of the world, slavery and servitude were simply a given in the biblical world of both the Old and New Testaments. Regardless of how this reality strikes our contemporary sensibilities, we must allow the biblical text to speak on its own terms within its own context.")]), _vm._v(" "), _c('h4', {
    staticClass: "c_head sigil_not_in_toc"
  }, [_vm._v("Servant, slave or none of the above?")]), _vm._v(" "), _c('p', {
    staticClass: "fo"
  }, [_vm._v("One significant challenge in studying this theme is the vocabulary and overlapping conceptual realms of slavery and servanthood. Both the Old and New Testaments use a range of words to capture these ideas, and often do so in ways that defy precise distinctions."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/SOTL_notes#FN_Ch01-8",
      "id": "FR_Ch01-8",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("8")])]), _vm._v(" As well as the fluidity of vocabulary used, there were significant differences in how slavery and servanthood were practised and perceived in different ancient cultures. In addition to these challenges, there is also the fact that the various "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("lxx")]), _vm._v(" translators use a variety of Greek terms to render Hebrew terms that refer to slavery and servanthood, even using two different Greek words to render one Hebrew word within the span of a few verses with no discernable nuance in meaning between the two."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/SOTL_notes#FN_Ch01-9",
      "id": "FR_Ch01-9",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("9")])])]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("This challenge is multiplied when one translates from Hebrew and/or Greek into a modern language such as English. The meaning of an individual word is determined by its use in context, and words have semantic ranges. For example, the Hebrew word "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("‘ebed")]), _vm._v(" occurs over 800 times in the Old Testament, but this same word can refer to someone who is a lowly and menial slave (Lev. 26:13) or a high-ranking official in the service of a king (Gen. 41:37–38). Even an English translation such as the English Standard Version ("), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("esvuk")]), _vm._v("), which strives for a ‘word-for-word’ translation that renders Hebrew words with the same English words where possible, rightly recognizes that the use of "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("‘ebed")]), _vm._v(" in 1 Kings 1:9 refers to Solomon’s high-ranking attendants and translates it as ‘officials’. Similar difficulties arise in the New Testament when attempting to translate "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("doulos")]), _vm._v(", as that term also allows for a range of meanings (e.g. slave, bondservant, servant) depending on the context and the nature of the relationship in view. According to the preface of the "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("esvuk")]), _vm._v(", where "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("doulos")]), _vm._v(" refers to ‘absolute ownership by a master’ they render it ‘slave’ (e.g. Rom. 6:16), but when a ‘more limited form of servitude is in view’ they translate it ‘bondservant’ (e.g. 1 Cor. 7:21–24) or ‘servant’ (e.g. John 4:51)."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/SOTL_notes#FN_Ch01-10",
      "id": "FR_Ch01-10",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("10")])]), _vm._v(" Although other English versions do not raise this issue in their preface, they regularly practise similar approaches."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/SOTL_notes#FN_Ch01-11",
      "id": "FR_Ch01-11",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("11")])])]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("As if those difficulties are not enough, there is the further challenge of accounting for how the original audiences of the Bible would have understood the language of servitude. As we have already noted, various forms of servitude were part of the fabric of both the ancient Near East and the Greco-Roman world. Based on the various ways that the Hebrew word "), _c('span', {
    staticClass: "translit_hebrew_ital"
  }, [_vm._v("ʿ")]), _c('em', {
    staticClass: "italic"
  }, [_vm._v("ebed")]), _vm._v(" (as well as other related terms) is used in the Old Testament, there is clear evidence that in certain contexts the term has an honorary sense. In other words, far from communicating a sense of bondage or oppression, it communicated a privileged position of authority and status."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/SOTL_notes#FN_Ch01-12",
      "id": "FR_Ch01-12",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("12")])]), _vm._v(" The work of scholars such as Gerhard Sass, Walter Ollrog and Dale Martin has shown that the Greek word "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("doulos")]), _vm._v(" could also refer to a person in a position of honour or leadership."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/SOTL_notes#FN_Ch01-13",
      "id": "FR_Ch01-13",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("13")])]), _vm._v(" There should be little doubt that when the New Testament writers used expressions such as ‘slave of Christ/God’ or ‘servant of Christ/God’ that many of the original readers/hearers initially understood such references through the framework of their cultural experience of slavery in the Greco-Roman world. This would have been especially true of Gentile converts who had little or no exposure to the Old Testament. However, that does not mean that the New Testament authors "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("intended")]), _vm._v(" such references to be "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("exclusively")]), _vm._v(" or even "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("primarily")]), _vm._v(" understood against this Greco-Roman cultural background."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/SOTL_notes#FN_Ch01-14",
      "id": "FR_Ch01-14",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("14")])]), _vm._v(" Indeed, there are often strong contextual indicators that expressions such as ‘slave of Christ/God’ or ‘servant of Christ/God’ have as their "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("primary")]), _vm._v(" referent the Old Testament background of key individuals raised up by God to further his creational and redemptive purposes in the world. Furthermore, the New Testament writers often take language and concepts that had certain meanings and connotations in the Greco-Roman world and reframe them in the light of the truth of the gospel such that they take on fresh significance."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/SOTL_notes#FN_Ch01-15",
      "id": "FR_Ch01-15",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("15")])]), _vm._v(" As the chapters that follow will demonstrate, that is certainly the case with the use of servant language.")]), _vm._v(" "), _c('h4', {
    staticClass: "c_head sigil_not_in_toc"
  }, [_vm._v("Approach of this study")]), _vm._v(" "), _c('p', {
    staticClass: "fo"
  }, [_vm._v("It is important to note that this is not a word study, as Scripture uses a variety of terms and expressions to both identify and describe these key individuals. There are a variety of different terms and expressions that together express the servant of the Lord theme. Indeed, as we will see, in some instances the biblical text portrays an individual or group of people acting as a servant of the Lord without explicitly using such terminology. Thus, our interest is in the concept of the servant of the Lord, not merely the expression or terminology itself."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "FnotRef",
    attrs: {
      "href": "#/Text/SOTL_notes#FN_Ch01-16",
      "id": "FR_Ch01-16",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("16")])])]), _vm._v(" "), _c('p', {
    staticClass: "pi"
  }, [_vm._v("Each chapter will address a specific individual who is identified as a servant of the Lord. Based on a careful reading of the biblical text (with insight from the broader historical, cultural and social contexts along the way), I will attempt to identify and summarize the nature of his role within God’s purposes for both creation and redemption. Along the way we will see the consistent pattern that God uses each individual servant to produce a servant people. Therefore, tracing this servant thread throughout Scripture sheds fresh light on (1) the role of these key figures in redemptive history; (2) how these key figures point forward to Christ; (3) the identity of God’s people; and (4) how we interact with fellow believers and the world around us.")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-c33c0f3e","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/Text/SOTL_ch01.vue
// module id = 78
// module chunks = 0